import { quantitySheetTemplate } from "../../core/pdf/templates/quantitySheetTemplate";
import { auditLogger } from "../../core/audit/auditLogger";
import { useProject } from "../../context/ProjectContext";

import React, { useState, useEffect } from 'react';
import { 
  FileText, HardHat, Layers, Printer, Plus, Trash2, 
  CheckCircle2, XCircle, Calculator, Save, Check
} from 'lucide-react';

export const CivilToolkit = () => {
  const { activeProject, saveRecordToActive } = useProject();
  const meta = activeProject?.metadata || {};
  const [activeTab, setActiveTab] = useState('minutes'); // minutes | materials | compaction
  const [savedAlert, setSavedAlert] = useState(false);

  // --- ۱. استیت‌های صورت‌جلسه احجام ---
  const [docMeta, setDocMeta] = useState({
    docNo: "101/1405",
    date: new Date().toLocaleDateString("fa-IR"),
    project: meta.title || "پروژه فعال کارگاهی",
    employer: meta.client || "اداره کل راهداری و حمل‌ونقل جاده‌ای",
    consultant: meta.consultant || "مهندسین مشاور سازه و راه",
    contractor: meta.contractor || "شرکت مهندسی و ساختمانی آکام",
    subject: "صورت‌جلسه احجام عملیات خاکی و بستركوبی زیرسازی",
    location: meta.location || "کیلومتر ۱۲ الی ۱۵ محور اصلی"
  });

  const [items, setItems] = useState([
    { id: 1, desc: 'خاکبرداری در زمین‌های دج و سنگی', length: 120, width: 8.5, height: 0.6, count: 1, unit: 'm³' },
    { id: 2, desc: 'بستربستر کوبی با تراکم ۹۵ درصد', length: 120, width: 8.5, height: 1, count: 1, unit: 'm²' }
  ]);

  // --- ۲. استیت‌های متره مصالح ---
  const [concreteVol, setConcreteVol] = useState(15);
  const [cementGrade, setCementGrade] = useState(350);

  const [rebarList, setRebarList] = useState([
    { id: 1, size: 16, length: 6, count: 40 },
    { id: 2, size: 20, length: 12, count: 20 }
  ]);

  // --- ۳. استیت‌های آزمایش تراکم خاک (Sand Cone) ---
  const [compaction, setCompaction] = useState({
    wInitialSand: 6500,  // وزن ماسه پر شده اولیه (گرم)
    wRemainSand: 2350,   // وزن ماسه باقیمانده (گرم)
    wCone: 1450,         // وزن ماسه در قیف (گرم)
    sandDensity: 1.42,   // دانسیته استاندارد ماسه (g/cm³)
    wWetSoil: 3820,      // وزن خاک مرطوب خروجی از چاله (گرم)
    moisture: 7.2,       // درصد رطوبت خاک (%)
    maxDryDensity: 2.12  // دانسیته ماکزیمم پروکتور آزمایشگاه (g/cm³)
  });

  // بازخوانی ذخیره‌سازی محلی
  useEffect(() => {
    const saved = localStorage.getItem('civil_toolkit_state');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.docMeta) setDocMeta(parsed.docMeta);
        if (parsed.items) setItems(parsed.items);
        if (parsed.compaction) setCompaction(parsed.compaction);
        if (parsed.rebarList) setRebarList(parsed.rebarList);
      } catch (e) {}
    }
  }, []);

  // ذخیره اطلاعات
  const handleSaveData = async () => {
    if (activeProject) {
      await saveRecordToActive("quantitySheet", {
        meta: docMeta,
        items,
        totalVolume: totalMinutesVolume
      });
      if (activeTab === "compaction") {
        await saveRecordToActive("soilTest", {
          ...compaction,
          relativeCompaction,
          isPassed
        });
      }
    }
    setSavedAlert(true);
    setTimeout(() => setSavedAlert(false), 2500);
  };

  // محاسبات جدول احجام
  const calculateVolume = (item) => {
    const vol = (parseFloat(item.length) || 0) * 
                (parseFloat(item.width) || 0) * 
                (parseFloat(item.height) || 1) * 
                (parseFloat(item.count) || 1);
    return Math.round(vol * 100) / 100;
  };

  const totalMinutesVolume = items.reduce((sum, item) => sum + calculateVolume(item), 0);

  // افزودن ردیف صورت‌جلسه
  const addItem = () => {
    setItems([...items, { id: Date.now(), desc: 'آیتم اجرایی جدید', length: 10, width: 1, height: 1, count: 1, unit: 'm³' }]);
  };

  const removeItem = (id) => {
    if (items.length > 1) setItems(items.filter(i => i.id !== id));
  };

  // محاسبات بتن
  const concreteCementKg = concreteVol * cementGrade;
  const concreteSandKg = concreteVol * 650;
  const concreteGravelKg = concreteVol * 1200;
  const concreteWaterLit = concreteVol * 180;

  // محاسبات میلگرد: w = (d^2 / 162) * L * N
  const calculateRebarWeight = (item) => {
    const unitWeight = (item.size * item.size) / 162;
    return Math.round(unitWeight * item.length * item.count * 10) / 10;
  };

  const totalRebarWeight = rebarList.reduce((sum, item) => sum + calculateRebarWeight(item), 0);

  // محاسبات تراکم خاک
  const wSandInHole = Math.max(0, compaction.wInitialSand - compaction.wRemainSand - compaction.wCone);
  const holeVolume = compaction.sandDensity > 0 ? (wSandInHole / compaction.sandDensity) : 0;
  const wetDensity = holeVolume > 0 ? (compaction.wWetSoil / holeVolume) : 0;
  const dryDensity = wetDensity / (1 + (compaction.moisture / 100));
  const relativeCompaction = compaction.maxDryDensity > 0 ? ((dryDensity / compaction.maxDryDensity) * 100) : 0;
  const isPassed = relativeCompaction >= 95.0;

  return (
    <div className="space-y-6">
      
      {/* هدر تب‌ها و دکمه ذخیره */}
      <div className="flex flex-wrap items-center justify-between gap-3 no-print">
        <div className="flex items-center gap-1.5 p-1 bg-slate-900/80 [body.light-theme_&]:bg-slate-200/80 rounded-xl border border-slate-800 [body.light-theme_&]:border-slate-300">
          <button
            onClick={() => setActiveTab('minutes')}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-bold transition ${activeTab === 'minutes' ? 'bg-sky-500 text-white shadow-md' : 'text-slate-400 hover:text-white'}`}
          >
            <FileText className="w-4 h-4" />
            <span>صورت‌جلسه احجام</span>
          </button>

          <button
            onClick={() => setActiveTab('materials')}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-bold transition ${activeTab === 'materials' ? 'bg-sky-500 text-white shadow-md' : 'text-slate-400 hover:text-white'}`}
          >
            <Calculator className="w-4 h-4" />
            <span>متره مصالح</span>
          </button>

          <button
            onClick={() => setActiveTab('compaction')}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-bold transition ${activeTab === 'compaction' ? 'bg-sky-500 text-white shadow-md' : 'text-slate-400 hover:text-white'}`}
          >
            <Layers className="w-4 h-4" />
            <span>تراکم خاک</span>
          </button>
        </div>

        <button
          onClick={handleSaveData}
          className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 border border-emerald-500/30 rounded-xl text-xs font-bold transition active:scale-95"
        >
          {savedAlert ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          <span>{savedAlert ? 'در حافظه ذخیره شد' : 'ذخیره تغییرات'}</span>
        </button>
      </div>

      {/* ۱. تب صورت‌جلسه کارگاهی با ساختار رسمی چاپ */}
      {activeTab === 'minutes' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between no-print">
            <h3 className="font-extrabold text-sm sm:text-base text-white [body.light-theme_&]:text-slate-900 flex items-center gap-2">
              <HardHat className="w-5 h-5 text-sky-400" />
              <span>تنظیم و صدور صورت‌جلسه کارگاهی</span>
            </h3>
            <button
              onClick={async () => {
      try {
        const records = projectRecords.filter(r => r.type === "quantitySheet");
        if(records.length === 0) return alert("ابتدا صورت‌جلسه را ذخیره کنید.");
        const latestRecord = records[0];
        const logs = await auditLogger.getByProject(activeProject.id);
        const recordLog = logs.find(l => l.entityId === latestRecord.rootId && l.revision === latestRecord.revision);
        quantitySheetTemplate.generate(activeProject, latestRecord, recordLog);
      } catch(e) {
        alert("خطا در تولید سند PDF: " + e.message);
      }
    }}
              className="flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-xl text-xs font-bold transition shadow-md active:scale-95"
            >
              <Printer className="w-4 h-4" />
              <span>چاپ / خروجی PDF استاندارد</span>
            </button>
          </div>

          {/* بدنه رسمی برگه صورت‌جلسه (A4 Printable Document) */}
          <div className="print-only-container bg-[#0b1329] [body.light-theme_&]:bg-white border border-slate-800 [body.light-theme_&]:border-slate-300 rounded-2xl p-5 sm:p-7 text-slate-100 [body.light-theme_&]:text-slate-900 shadow-xl space-y-6">
            
            {/* سربرگ برگه */}
            <div className="border-b-2 border-slate-700 [body.light-theme_&]:border-slate-400 pb-5">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-3 text-center sm:text-right">
                <div>
                  <h2 className="font-black text-lg sm:text-xl text-white [body.light-theme_&]:text-slate-900">صورت‌جلسه کارگاهی و احجام اجرایی</h2>
                  <p className="text-xs text-slate-400 [body.light-theme_&]:text-slate-600 mt-1">پیوست اسناد فنی، تحویل و رسیدگی مالی پیمان</p>
                </div>
                <div className="text-xs font-mono space-y-1 bg-slate-900/60 [body.light-theme_&]:bg-slate-100 p-2.5 rounded-xl border border-slate-800 [body.light-theme_&]:border-slate-300">
                  <div>شماره سند: <input value={docMeta.docNo} onChange={e => setDocMeta({...docMeta, docNo: e.target.value})} className="bg-transparent font-bold border-b border-dotted w-24 text-center focus:outline-none" /></div>
                  <div>تاریخ تنظیم: <input value={docMeta.date} onChange={e => setDocMeta({...docMeta, date: e.target.value})} className="bg-transparent font-bold border-b border-dotted w-24 text-center focus:outline-none" /></div>
                </div>
              </div>

              {/* مشخصات قرارداد و محل */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4 text-xs bg-slate-900/40 [body.light-theme_&]:bg-slate-50 p-3 rounded-xl">
                <div><span className="text-slate-400 [body.light-theme_&]:text-slate-600">پروژه:</span> <input value={docMeta.project} onChange={e => setDocMeta({...docMeta, project: e.target.value})} className="bg-transparent font-bold w-4/5 focus:outline-none" /></div>
                <div><span className="text-slate-400 [body.light-theme_&]:text-slate-600">کارفرما:</span> <input value={docMeta.employer} onChange={e => setDocMeta({...docMeta, employer: e.target.value})} className="bg-transparent font-bold w-4/5 focus:outline-none" /></div>
                <div><span className="text-slate-400 [body.light-theme_&]:text-slate-600">دستگاه نظارت:</span> <input value={docMeta.consultant} onChange={e => setDocMeta({...docMeta, consultant: e.target.value})} className="bg-transparent font-bold w-4/5 focus:outline-none" /></div>
                <div><span className="text-slate-400 [body.light-theme_&]:text-slate-600">پیمانکار:</span> <input value={docMeta.contractor} onChange={e => setDocMeta({...docMeta, contractor: e.target.value})} className="bg-transparent font-bold w-4/5 focus:outline-none" /></div>
                <div className="sm:col-span-2"><span className="text-slate-400 [body.light-theme_&]:text-slate-600">محل دقیق اجرا:</span> <input value={docMeta.location} onChange={e => setDocMeta({...docMeta, location: e.target.value})} className="bg-transparent font-bold w-4/5 focus:outline-none" /></div>
              </div>
            </div>

            {/* جدول داینامیک احجام اجرایی */}
            <div className="overflow-x-auto">
              <table className="print-table w-full text-right text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-800/80 [body.light-theme_&]:bg-slate-200 text-slate-300 [body.light-theme_&]:text-slate-700">
                    <th className="p-2 border border-slate-700 [body.light-theme_&]:border-slate-300 text-center w-10">ردیف</th>
                    <th className="p-2 border border-slate-700 [body.light-theme_&]:border-slate-300">شرح دقیق عملیات اجرایی</th>
                    <th className="p-2 border border-slate-700 [body.light-theme_&]:border-slate-300 text-center w-16">طول (m)</th>
                    <th className="p-2 border border-slate-700 [body.light-theme_&]:border-slate-300 text-center w-16">عرض (m)</th>
                    <th className="p-2 border border-slate-700 [body.light-theme_&]:border-slate-300 text-center w-16">ارتفاع (m)</th>
                    <th className="p-2 border border-slate-700 [body.light-theme_&]:border-slate-300 text-center w-14">تعداد</th>
                    <th className="p-2 border border-slate-700 [body.light-theme_&]:border-slate-300 text-center w-20">حجم نهایی</th>
                    <th className="p-2 border border-slate-700 [body.light-theme_&]:border-slate-300 text-center w-10 no-print">حذف</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={item.id} className="border-b border-slate-800 [body.light-theme_&]:border-slate-200 hover:bg-slate-800/30">
                      <td className="p-2 text-center font-mono">{idx + 1}</td>
                      <td className="p-1">
                        <input value={item.desc} onChange={e => {
                          const updated = [...items]; updated[idx].desc = e.target.value; setItems(updated);
                        }} className="w-full bg-transparent px-1 py-1 focus:outline-none" />
                      </td>
                      <td className="p-1"><input type="number" value={item.length} onChange={e => {
                        const updated = [...items]; updated[idx].length = e.target.value; setItems(updated);
                      }} className="w-full bg-transparent text-center font-mono focus:outline-none" /></td>
                      <td className="p-1"><input type="number" value={item.width} onChange={e => {
                        const updated = [...items]; updated[idx].width = e.target.value; setItems(updated);
                      }} className="w-full bg-transparent text-center font-mono focus:outline-none" /></td>
                      <td className="p-1"><input type="number" value={item.height} onChange={e => {
                        const updated = [...items]; updated[idx].height = e.target.value; setItems(updated);
                      }} className="w-full bg-transparent text-center font-mono focus:outline-none" /></td>
                      <td className="p-1"><input type="number" value={item.count} onChange={e => {
                        const updated = [...items]; updated[idx].count = e.target.value; setItems(updated);
                      }} className="w-full bg-transparent text-center font-mono focus:outline-none" /></td>
                      <td className="p-2 text-center font-mono font-bold text-sky-400 [body.light-theme_&]:text-sky-700">
                        {calculateVolume(item)}
                      </td>
                      <td className="p-1 text-center no-print">
                        <button onClick={() => removeItem(item.id)} className="text-rose-400 hover:text-rose-300 p-1">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-900/70 [body.light-theme_&]:bg-slate-100 font-bold border-t-2 border-slate-700 [body.light-theme_&]:border-slate-300">
                    <td colSpan="6" className="p-2.5 text-left pl-4">مجموع احجام عملیاتی:</td>
                    <td className="p-2.5 text-center font-mono text-sm text-sky-400 [body.light-theme_&]:text-sky-700">{totalMinutesVolume} m³</td>
                    <td className="no-print"></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="no-print pt-2">
              <button onClick={addItem} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-500/10 border border-sky-500/30 text-sky-400 text-xs font-bold hover:bg-sky-500/20 transition">
                <Plus className="w-3.5 h-3.5" />
                <span>افزودن ردیف کارکرد جدید</span>
              </button>
            </div>

            {/* کادر امضای رسمی سه‌جانبه در پرینت */}
            <div className="grid grid-cols-3 gap-4 pt-10 border-t border-slate-700 [body.light-theme_&]:border-slate-300 text-center text-xs">
              <div className="space-y-12">
                <p className="font-bold">نماینده پیمانکار</p>
                <div className="text-[10px] text-slate-400 [body.light-theme_&]:text-slate-500">امضا و مهر</div>
              </div>
              <div className="space-y-12">
                <p className="font-bold">دستگاه نظارت مقیم</p>
                <div className="text-[10px] text-slate-400 [body.light-theme_&]:text-slate-500">امضا و مهر</div>
              </div>
              <div className="space-y-12">
                <p className="font-bold">نماینده کارفرما</p>
                <div className="text-[10px] text-slate-400 [body.light-theme_&]:text-slate-500">امضا و مهر</div>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* ۲. تب متره مصالح (بتن و میلگرد) */}
      {activeTab === 'materials' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* محاسبه بتن و اجزا */}
          <div className="bg-[#0b1329] [body.light-theme_&]:bg-white border border-slate-800 [body.light-theme_&]:border-slate-300 p-5 rounded-2xl space-y-4">
            <h3 className="font-bold text-sm text-white [body.light-theme_&]:text-slate-900 flex items-center gap-2 border-b border-slate-800 [body.light-theme_&]:border-slate-200 pb-3">
              <Calculator className="w-4 h-4 text-sky-400" />
              <span>محاسبه مصالح بتن‌ریزی</span>
            </h3>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <label className="text-slate-400 [body.light-theme_&]:text-slate-600 block mb-1">حجم بتن (m³):</label>
                <input type="number" value={concreteVol} onChange={e => setConcreteVol(parseFloat(e.target.value) || 0)} className="w-full bg-slate-900 [body.light-theme_&]:bg-slate-100 border border-slate-700 [body.light-theme_&]:border-slate-300 rounded-lg p-2 font-mono" />
              </div>
              <div>
                <label className="text-slate-400 [body.light-theme_&]:text-slate-600 block mb-1">عیار سیمان (kg/m³):</label>
                <input type="number" value={cementGrade} onChange={e => setCementGrade(parseFloat(e.target.value) || 0)} className="w-full bg-slate-900 [body.light-theme_&]:bg-slate-100 border border-slate-700 [body.light-theme_&]:border-slate-300 rounded-lg p-2 font-mono" />
              </div>
            </div>

            <div className="space-y-2 pt-2 text-xs">
              <div className="flex justify-between p-2 rounded-lg bg-slate-900/60 [body.light-theme_&]:bg-slate-50">
                <span>سیمان مصرفی:</span>
                <span className="font-mono font-bold text-sky-400 [body.light-theme_&]:text-sky-600">{concreteCementKg.toLocaleString()} kg ({(concreteCementKg / 1000).toFixed(2)} تن)</span>
              </div>
              <div className="flex justify-between p-2 rounded-lg bg-slate-900/60 [body.light-theme_&]:bg-slate-50">
                <span>ماسه برآورد شده:</span>
                <span className="font-mono font-bold">{concreteSandKg.toLocaleString()} kg</span>
              </div>
              <div className="flex justify-between p-2 rounded-lg bg-slate-900/60 [body.light-theme_&]:bg-slate-50">
                <span>شن برآورد شده:</span>
                <span className="font-mono font-bold">{concreteGravelKg.toLocaleString()} kg</span>
              </div>
              <div className="flex justify-between p-2 rounded-lg bg-slate-900/60 [body.light-theme_&]:bg-slate-50">
                <span>آب اختلاط تخمینی:</span>
                <span className="font-mono font-bold">{concreteWaterLit.toLocaleString()} لیتر</span>
              </div>
            </div>
          </div>

          {/* محاسبه وزن میلگرد (اشتال) */}
          <div className="bg-[#0b1329] [body.light-theme_&]:bg-white border border-slate-800 [body.light-theme_&]:border-slate-300 p-5 rounded-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-slate-800 [body.light-theme_&]:border-slate-200 pb-3">
              <h3 className="font-bold text-sm text-white [body.light-theme_&]:text-slate-900 flex items-center gap-2">
                <Layers className="w-4 h-4 text-sky-400" />
                <span>محاسبه وزن میلگرد (اشتال)</span>
              </h3>
              <button onClick={() => setRebarList([...rebarList, { id: Date.now(), size: 14, length: 12, count: 10 }])} className="text-xs text-sky-400 font-bold flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> ردیف جدید
              </button>
            </div>

            <div className="space-y-2 max-h-60 overflow-y-auto">
              {rebarList.map((rb, idx) => (
                <div key={rb.id} className="flex items-center gap-2 text-xs bg-slate-900/50 [body.light-theme_&]:bg-slate-50 p-2 rounded-lg border border-slate-800/80 [body.light-theme_&]:border-slate-200">
                  <span className="text-slate-400">Φ:</span>
                  <select value={rb.size} onChange={e => {
                    const upd = [...rebarList]; upd[idx].size = parseInt(e.target.value); setRebarList(upd);
                  }} className="bg-slate-800 [body.light-theme_&]:bg-white rounded px-1.5 py-1 font-mono">
                    {[8, 10, 12, 14, 16, 18, 20, 22, 25, 28, 32].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>

                  <span className="text-slate-400">طول:</span>
                  <input type="number" value={rb.length} onChange={e => {
                    const upd = [...rebarList]; upd[idx].length = parseFloat(e.target.value) || 0; setRebarList(upd);
                  }} className="w-14 bg-slate-800 [body.light-theme_&]:bg-white text-center rounded p-1 font-mono" placeholder="طول" />
                  
                  <span className="text-slate-400">تعداد:</span>
                  <input type="number" value={rb.count} onChange={e => {
                    const upd = [...rebarList]; upd[idx].count = parseInt(e.target.value) || 0; setRebarList(upd);
                  }} className="w-14 bg-slate-800 [body.light-theme_&]:bg-white text-center rounded p-1 font-mono" placeholder="تعداد" />

                  <span className="font-mono font-bold text-sky-400 mr-auto">{calculateRebarWeight(rb)} kg</span>
                  <button onClick={() => setRebarList(rebarList.filter(r => r.id !== rb.id))} className="text-rose-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>

            <div className="pt-3 border-t border-slate-800 [body.light-theme_&]:border-slate-200 flex justify-between items-center text-xs font-bold">
              <span>وزن کل میلگردها:</span>
              <span className="text-base font-mono text-sky-400 [body.light-theme_&]:text-sky-600">{totalRebarWeight.toLocaleString()} kg ({(totalRebarWeight / 1000).toFixed(3)} تن)</span>
            </div>
          </div>

        </div>
      )}

      {/* ۳. تب محاسبات آزمایش تراکم خاک کارگاه */}
      {activeTab === 'compaction' && (
        <div className="bg-[#0b1329] [body.light-theme_&]:bg-white border border-slate-800 [body.light-theme_&]:border-slate-300 p-5 sm:p-6 rounded-2xl space-y-6">
          <div className="flex items-center justify-between border-b border-slate-800 [body.light-theme_&]:border-slate-200 pb-3">
            <h3 className="font-bold text-sm sm:text-base text-white [body.light-theme_&]:text-slate-900 flex items-center gap-2">
              <Layers className="w-5 h-5 text-sky-400" />
              <span>آزمایش تعیین وزن مخصوص و درصد تراکم خاک (دانسیته مخروط ماسه)</span>
            </h3>
            <span className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${isPassed ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : 'bg-rose-500/10 text-rose-400 border border-rose-500/30'}`}>
              {isPassed ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
              <span>{isPassed ? 'تراکم مورد تایید (≥ ۹۵٪)' : 'عدم تایید تراکم (< ۹۵٪)'}</span>
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
            <div>
              <label className="text-slate-400 [body.light-theme_&]:text-slate-600 block mb-1">وزن ماسه اولیه + بطری (g):</label>
              <input type="number" value={compaction.wInitialSand} onChange={e => setCompaction({...compaction, wInitialSand: parseFloat(e.target.value) || 0})} className="w-full bg-slate-900 [body.light-theme_&]:bg-slate-100 border border-slate-700 [body.light-theme_&]:border-slate-300 rounded-lg p-2.5 font-mono" />
            </div>

            <div>
              <label className="text-slate-400 [body.light-theme_&]:text-slate-600 block mb-1">وزن ماسه باقیمانده + بطری (g):</label>
              <input type="number" value={compaction.wRemainSand} onChange={e => setCompaction({...compaction, wRemainSand: parseFloat(e.target.value) || 0})} className="w-full bg-slate-900 [body.light-theme_&]:bg-slate-100 border border-slate-700 [body.light-theme_&]:border-slate-300 rounded-lg p-2.5 font-mono" />
            </div>

            <div>
              <label className="text-slate-400 [body.light-theme_&]:text-slate-600 block mb-1">وزن ماسه در قیف (g):</label>
              <input type="number" value={compaction.wCone} onChange={e => setCompaction({...compaction, wCone: parseFloat(e.target.value) || 0})} className="w-full bg-slate-900 [body.light-theme_&]:bg-slate-100 border border-slate-700 [body.light-theme_&]:border-slate-300 rounded-lg p-2.5 font-mono" />
            </div>

            <div>
              <label className="text-slate-400 [body.light-theme_&]:text-slate-600 block mb-1">دانسیته ماسه استاندارد (g/cm³):</label>
              <input type="number" step="0.01" value={compaction.sandDensity} onChange={e => setCompaction({...compaction, sandDensity: parseFloat(e.target.value) || 0})} className="w-full bg-slate-900 [body.light-theme_&]:bg-slate-100 border border-slate-700 [body.light-theme_&]:border-slate-300 rounded-lg p-2.5 font-mono" />
            </div>

            <div>
              <label className="text-slate-400 [body.light-theme_&]:text-slate-600 block mb-1">وزن خاک مرطوب خروجی (g):</label>
              <input type="number" value={compaction.wWetSoil} onChange={e => setCompaction({...compaction, wWetSoil: parseFloat(e.target.value) || 0})} className="w-full bg-slate-900 [body.light-theme_&]:bg-slate-100 border border-slate-700 [body.light-theme_&]:border-slate-300 rounded-lg p-2.5 font-mono" />
            </div>

            <div>
              <label className="text-slate-400 [body.light-theme_&]:text-slate-600 block mb-1">درصد رطوبت خاک (%):</label>
              <input type="number" step="0.1" value={compaction.moisture} onChange={e => setCompaction({...compaction, moisture: parseFloat(e.target.value) || 0})} className="w-full bg-slate-900 [body.light-theme_&]:bg-slate-100 border border-slate-700 [body.light-theme_&]:border-slate-300 rounded-lg p-2.5 font-mono" />
            </div>

            <div className="sm:col-span-2 lg:col-span-3">
              <label className="text-slate-400 [body.light-theme_&]:text-slate-600 block mb-1">حداکثر وزن مخصوص خشک آزمایشگاه - پروکتور (g/cm³):</label>
              <input type="number" step="0.01" value={compaction.maxDryDensity} onChange={e => setCompaction({...compaction, maxDryDensity: parseFloat(e.target.value) || 0})} className="w-full max-w-sm bg-slate-900 [body.light-theme_&]:bg-slate-100 border border-slate-700 [body.light-theme_&]:border-slate-300 rounded-lg p-2.5 font-mono" />
            </div>
          </div>

          {/* پنل نتایج نهایی محاسبات تراکم */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4 border-t border-slate-800 [body.light-theme_&]:border-slate-200">
            <div className="bg-slate-900/60 [body.light-theme_&]:bg-slate-50 p-3 rounded-xl border border-slate-800 [body.light-theme_&]:border-slate-200 text-center">
              <div className="text-[10px] text-slate-400 [body.light-theme_&]:text-slate-500">حجم چاله</div>
              <div className="font-mono font-bold text-sm text-slate-200 [body.light-theme_&]:text-slate-800 mt-1">{Math.round(holeVolume)} cm³</div>
            </div>

            <div className="bg-slate-900/60 [body.light-theme_&]:bg-slate-50 p-3 rounded-xl border border-slate-800 [body.light-theme_&]:border-slate-200 text-center">
              <div className="text-[10px] text-slate-400 [body.light-theme_&]:text-slate-500">دانسیته مرطوب خاک</div>
              <div className="font-mono font-bold text-sm text-slate-200 [body.light-theme_&]:text-slate-800 mt-1">{wetDensity.toFixed(3)} g/cm³</div>
            </div>

            <div className="bg-slate-900/60 [body.light-theme_&]:bg-slate-50 p-3 rounded-xl border border-slate-800 [body.light-theme_&]:border-slate-200 text-center">
              <div className="text-[10px] text-slate-400 [body.light-theme_&]:text-slate-500">دانسیته خشک خاک</div>
              <div className="font-mono font-bold text-sm text-slate-200 [body.light-theme_&]:text-slate-800 mt-1">{dryDensity.toFixed(3)} g/cm³</div>
            </div>

            <div className={`p-3 rounded-xl border text-center ${isPassed ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-400' : 'bg-rose-500/15 border-rose-500/40 text-rose-400'}`}>
              <div className="text-[10px] font-bold">درصد تراکم نسبی (RC)</div>
              <div className="font-mono font-black text-base mt-1">{relativeCompaction.toFixed(1)}%</div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CivilToolkit;
